import { Component, OnInit, OnDestroy } from '@angular/core'
import { AdminService } from '../../../services/admin.service'
import { InvoiceStatus, Merchant, Parking, parkingsPath, ParkingStatus, SubscriptionType, EnumNames } from '@parkupp/core'
import { collection, collectionData, doc, orderBy, query, Timestamp, updateDoc, where, Query, DocumentData } from '@angular/fire/firestore'
import { GlobalService } from 'src/app/services/global.service'
import { environment } from 'src/environments/environment'
import { Router } from '@angular/router'
import { ParkingService } from 'src/app/services/parking.service'
import { MerchantService } from 'src/app/services/merchant.service'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { Subscription } from 'rxjs'

type ParkingFilter = 'ALL' | ParkingStatus

@Component({
    selector: 'app-admin-list-parkings-page',
    templateUrl: './admin-list-parkings-page.component.html',
    styleUrls: ['./admin-list-parkings-page.component.scss'],
})
export class AdminListParkingsPageComponent implements OnInit, OnDestroy {
    EnumNames = EnumNames
    parkings: Parking[]
    allParkings: Parking[] = []
    parkingStatus = ParkingStatus
    selectedParking: Parking
    parkingStatuses = Object.values(ParkingStatus)
    merchants: Merchant[]
    activeFilter: ParkingFilter = ParkingStatus.PUBLISHED
    searchQuery: string = ''
    filteredParkings: Parking[]
    private enterpriseSubscription: Subscription

    constructor(
        private adminSvc: AdminService, 
        private globalService: GlobalService, 
        private router: Router, 
        private parkingService: ParkingService, 
        private merchantService: MerchantService,
        private enterpriseContext: EnterpriseContextService
    ) {}

    async ngOnInit() {
        this.merchants = await this.merchantService.listPromise()

        // Fetch all parkings for stats
        await this.fetchAllParkings()

        // Apply initial filter
        await this.fetchParkings()
        
        // Subscribe to enterprise context changes
        this.enterpriseSubscription = this.enterpriseContext.currentEnterprise$.subscribe(async () => {
            this.merchants = await this.merchantService.listPromise()
            await this.fetchAllParkings()
            await this.fetchParkings()
        })
    }
    
    ngOnDestroy(): void {
        if (this.enterpriseSubscription) {
            this.enterpriseSubscription.unsubscribe()
        }
    }

    async fetchAllParkings() {
        // Fetch all parkings without status filter for stats
        const allStatuses = Object.values(ParkingStatus)
        const allParkingsPromises = allStatuses.map((status) => this.parkingService.listPromise({ status }))
        const results = await Promise.all(allParkingsPromises)
        this.allParkings = results.flat()

        this.allParkings.forEach((parking) => {
            if (parking.merchantRef) {
                parking.$merchant = this.merchants.find((merchant) => merchant.$key === parking.merchantRef.id)
            }
        })
    }

    setFilter(filter: ParkingFilter) {
        this.activeFilter = filter
        this.fetchParkings()
    }

    async fetchParkings() {
        if (this.activeFilter === 'ALL') {
            const results = await Promise.all(
                Object.values(ParkingStatus).map((status) => this.parkingService.listPromise({ status }))
            )
            this.parkings = results.flat()
        } else {
            this.parkings = await this.parkingService.listPromise({ status: this.activeFilter })
        }

        this.parkings.forEach((parking) => {
            if (parking.merchantRef) {
                parking.$merchant = this.merchants.find((merchant) => merchant.$key === parking.merchantRef.id)
            }
        })

        this.parkings.sort((a, b) => b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime())
        this.applySearch()
    }

    applySearch() {
        if (!this.searchQuery?.trim()) {
            this.filteredParkings = this.parkings
            return
        }
        const q = this.searchQuery.toLowerCase().trim()
        this.filteredParkings = this.parkings.filter((p) =>
            p.title?.toLowerCase().includes(q) ||
            p.denormMerchantCompanyName?.toLowerCase().includes(q) ||
            p.parkingLocation?.city?.toLowerCase().includes(q) ||
            p.parkingLocation?.suburb?.toLowerCase().includes(q) ||
            p.$key?.toLowerCase().includes(q)
        )
    }

    approveParking(key: any) {
        return new Promise<any>((resolve) => {
            const reference = doc(this.adminSvc.firestore, parkingsPath(), key)
            updateDoc(reference, {
                parkingStatus: ParkingStatus.PUBLISHED,
            })
        })
    }

    async showContextMenu(event: MouseEvent, parking: Parking) {
        this.globalService.contextMenuItems = []
        this.selectedParking = parking
        this.globalService.showContextMenu(event)

        const leadSubMenuItems: any = this.parkingStatuses.map((status) => {
            return {
                label: status,
                action: () => this.updateParkingStatus(parking, status as ParkingStatus),
            }
        })
        this.globalService.contextMenuItems.push({
            label: 'Update Parking Status',
            icon: 'fa fa-file',
            subMenuItems: leadSubMenuItems,
        })

        this.globalService.contextMenuItems.push({
            icon: 'fa fa-eye',
            label: 'View Parking',
            action: () => this.router.navigate(['/parking', parking.$key]),
        })
        this.globalService.contextMenuItems.push({
            icon: 'fa fa-edit',
            label: 'Edit Parking',
            action: () => this.router.navigate(['/merchant', parking.merchantRef?.id, 'parking', parking.$key]),
        })
        this.globalService.contextMenuItems.push({
            icon: 'fa fa-link',
            label: 'Copy PayFast Special Link',
            action: () => this.copyPayFastSpecialLink(),
        })

        this.globalService.contextMenuItems.push({
            icon: 'fa fa-link',
            label: 'Copy EFT Special Link',
            action: () => this.copyEftSpecialLink(),
        })
    }

    copyPayFastSpecialLink() {
        const url = `${environment.host}/parking/${this.selectedParking.$key}?allowPaymentType=${SubscriptionType.PAYFAST}`
        navigator.clipboard.writeText(url).then(() => {
            console.log('URL copied to clipboard:', url)
        })
    }

    copyEftSpecialLink() {
        const url = `${environment.host}/parking/${this.selectedParking.$key}?allowPaymentType=${SubscriptionType.EFT}`
        navigator.clipboard.writeText(url).then(() => {
            console.log('URL copied to clipboard:', url)
        })
    }

    async updateParkingStatus(parking: Parking, status: ParkingStatus) {
        parking.parkingStatus = status
        await this.parkingService.updateFields(parking.merchantRef?.id!, parking.$key!, { parkingStatus: status })
        await this.fetchParkings()
    }

    getDaysAgo(date: any) {
        return (new Date().getTime() - new Date(date.toDate()).getTime()) / (1000 * 3600 * 24)
    }

    getTotalParkings(): number {
        return this.allParkings.length
    }

    getPublishedCount(): number {
        return this.allParkings.filter((p) => p.parkingStatus === ParkingStatus.PUBLISHED).length
    }

    getDraftCount(): number {
        return this.allParkings.filter((p) => 
            p.parkingStatus === ParkingStatus.NEW || 
            p.parkingStatus === ParkingStatus.PENDING || 
            p.parkingStatus === ParkingStatus.PRIVATE
        ).length
    }

    getDeletedCount(): number {
        return this.allParkings.filter((p) => p.parkingStatus === ParkingStatus.DISABLED || p.deletedAt).length
    }

    getNoAddressCount(): number {
        return this.allParkings.filter((p) => !p.parkingLocation?.latitude || !p.parkingLocation?.longitude).length
    }
}
